import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { fmtEur, fmtDate, monthLabel } from "@/lib/format";
import { Printer, FileDown, X, Phone, Mail, Euro, StickyNote } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { k: "calls", label: "Telefoongesprekken & notities" },
  { k: "letters", label: "Verzonden brieven / e-mails / SMS" },
  { k: "payments", label: "Betalingshistorie" },
  { k: "penalties", label: "Boeteberekeningen" },
  { k: "contract", label: "Contractdetails" },
];

export default function ReportDialog({ open, onOpenChange, tenantId }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({ calls: true, letters: true, payments: true, penalties: true, contract: true });

  useEffect(() => {
    if (open && tenantId) api.get(`/tenants/${tenantId}/report`).then((r) => setData(r.data)).catch(() => {});
  }, [open, tenantId]);

  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));

  const downloadPdf = async () => {
    try {
      const sections = Object.keys(sel).filter((k) => sel[k]).join(",");
      const res = await api.get(`/tenants/${tenantId}/report/pdf?sections=${sections}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dossier_${t?.last_name || "huurder"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF gedownload");
    } catch (e) {
      toast.error("PDF genereren mislukt");
    }
  };

  const events = (data?.events || []).filter((e) => {
    if (e.kind === "call") return sel.calls;
    if (e.kind === "letter") return sel.letters;
    if (e.kind === "payment") return sel.payments;
    return true;
  });

  const icon = (kind) => kind === "call" ? Phone : kind === "letter" ? Mail : kind === "payment" ? Euro : StickyNote;
  const t = data?.tenant;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 gap-0 max-h-[92vh] flex flex-col overflow-hidden">
        <DialogTitle className="sr-only">Juridisch dossier rapport</DialogTitle>
        <DialogDescription className="sr-only">Selecteer onderdelen en druk het rapport af of sla het op als PDF.</DialogDescription>
        <div className="flex items-center justify-between p-4 border-b no-print bg-slate-50">
          <div>
            <h3 className="font-heading font-bold text-slate-900">Juridisch Dossier / Rapport</h3>
            <p className="text-xs text-slate-500">Vink aan welke onderdelen in het rapport moeten komen</p>
          </div>
          <button onClick={() => onOpenChange(false)}><X className="h-5 w-5 text-slate-500" /></button>
        </div>

        <div className="flex flex-wrap gap-3 p-4 border-b no-print bg-white">
          {SECTIONS.map((s) => (
            <label key={s.k} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={sel[s.k]} onCheckedChange={() => toggle(s.k)} data-testid={`report-section-${s.k}`} />
              {s.label}
            </label>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 p-2 bg-slate-100">
          {/* Printable area */}
          <div id="print-area" className="bg-white mx-auto max-w-3xl p-8 shadow-sm text-slate-800">
            <div className="flex justify-between items-start border-b-2 border-slate-800 pb-4 mb-6">
              <div>
                <h1 className="font-heading text-2xl font-bold">Dossier Betalingsachterstand</h1>
                <p className="text-sm text-slate-500">Praktisch-Debiteurenbeheer — vertrouwelijk juridisch document</p>
              </div>
              <div className="text-right text-xs text-slate-500">
                <div>Opgesteld: {fmtDate(data?.generated_at)}</div>
                <div>Beheerder: {data?.manager?.name || data?.manager?.email}</div>
              </div>
            </div>

            {t && (
              <div className="mb-6">
                <h2 className="font-heading font-bold text-lg mb-1">{t.first_name} {t.last_name}</h2>
                <p className="text-sm text-slate-600">{t.naw_street} {t.naw_number}, {t.naw_postal} {t.naw_city}</p>
                <p className="text-sm text-slate-600">{t.email1} {t.mobile1 && `• ${t.mobile1}`}</p>
                {t.client_name && <p className="text-sm text-slate-500">Opdrachtgever: {t.client_name}</p>}
                <p className="text-sm font-semibold text-red-600 mt-2">Totaal openstaand incl. boete: {fmtEur(t.outstanding)}</p>
              </div>
            )}

            {sel.contract && t?.contract && (
              <div className="mb-6 bg-slate-50 rounded-lg p-4 border">
                <h3 className="font-heading font-semibold mb-2">Contractgegevens</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Ingangsdatum: <b>{fmtDate(t.contract.start_date)}</b></div>
                  <div>Maandhuur: <b>{fmtEur(t.contract.monthly_rent)}</b></div>
                  <div>Boete per dag: <b>{fmtEur(t.contract.penalty_per_day)}</b></div>
                  <div>Max. boete/maand: <b>{fmtEur(t.contract.max_penalty_per_month)}</b></div>
                </div>
              </div>
            )}

            <h3 className="font-heading font-semibold mb-3">Communicatie & Betalingen — Tijdslijn</h3>
            <div className="space-y-3">
              {events.length === 0 && <p className="text-sm text-slate-400">Geen items geselecteerd.</p>}
              {events.map((e, i) => {
                const Icon = icon(e.kind);
                return (
                  <div key={i} className="flex gap-3 border-b border-slate-100 pb-3">
                    <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <span className="font-semibold text-sm">{e.title}</span>
                        <span className="text-xs text-slate-400">{fmtDate(e.date)}</span>
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{e.description}</p>
                      {e.kind === "payment" && sel.penalties && e.meta?.penalty > 0 && (
                        <p className="text-xs text-red-600">Berekende boete: {fmtEur(e.meta.penalty)}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-8 border-t pt-3">Dit document is automatisch gegenereerd en dient als onderbouwing bij een eventuele juridische procedure.</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t no-print bg-slate-50">
          <Button variant="outline" onClick={() => window.print()} data-testid="report-print-button"><Printer className="h-4 w-4 mr-2" /> Afdrukken</Button>
          <Button className="bg-blue-900 hover:bg-blue-800" onClick={downloadPdf} data-testid="report-export-pdf-button"><FileDown className="h-4 w-4 mr-2" /> Download PDF</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
